import {
  ColorAccentDefault,
  ColorContentReduced,
  ColorCriticalDefault,
  ColorIllustrationNeutral5,
  ColorWarningDefault,
} from '@moia-dev/pace-core-web/moia/pace-colors-light'
import type { TFunction } from 'i18next'

// Driver voice notes, keyed by the proto enum name. A severity the proto gains later
// falls through to ANNOTATION_FALLBACK_COLOR rather than rendering an invisible marker.
// Exported so the Annotations button can build its severity-filter checkboxes and
// color-swatch legend from the same source instead of duplicating the palette.
//
// Taken from pace's light palette to match the design: accent blue, warning
// amber, critical red and reduced-content grey. Plain strings rather than theme
// reads, because map markers are DOM that MapLibre owns, built outside React.
export const ANNOTATION_SEVERITY_COLORS: Record<string, string> = {
  TRIP_ANNOTATION_SEVERITY_LOW: ColorAccentDefault,
  TRIP_ANNOTATION_SEVERITY_MEDIUM: ColorWarningDefault,
  TRIP_ANNOTATION_SEVERITY_HIGH: ColorCriticalDefault,
  TRIP_ANNOTATION_SEVERITY_NON_PERFORMANT: ColorContentReduced,
}
// A lighter grey than non-performant's, so an unknown severity (or the triage
// map's "unclassified") is not mistaken for a non-performant note.
export const ANNOTATION_FALLBACK_COLOR = ColorIllustrationNeutral5

/**
 * The colour a note's severity is drawn in — the marker glyph and the popup's
 * severity dot both read it here, so the two can never disagree.
 */
export const annotationSeverityColor = (severity: string): string =>
  ANNOTATION_SEVERITY_COLORS[severity] ?? ANNOTATION_FALLBACK_COLOR

/**
 * How opaque a resolved note is drawn: its marker on every map, and the
 * severity circle in its popup.
 *
 * Faded rather than recoloured. Greys are already taken: the darker one means
 * non-performant, and the lighter fallback marks an unknown severity and the
 * triage map's "unclassified". A new hue would collide with one of them. A
 * faded pin keeps its severity colour, so a fixed High still reads as a High,
 * just as one that no longer needs looking at.
 */
export const ANNOTATION_RESOLVED_OPACITY = 0.4

/**
 * The severities the dropdown renders a checkbox for. A severity outside this set
 * has no checkbox to hide it behind, so `filterAnnotationsBySeverity` lets it
 * through — see that function for why.
 */
export const KNOWN_ANNOTATION_SEVERITIES = Object.keys(
  ANNOTATION_SEVERITY_COLORS,
)

// Switches rather than a key -> label map, because i18next-scanner only resolves keys
// that reach `t()` as string literals: a key looked up through a Record is invisible to
// it and `pnpm scan-translations` deletes it. Same reason
// `virtual-testing/ScenarioAuthoring/eventLabels.ts` is written this way.
export const annotationSeverityLabel = (
  t: TFunction,
  severity: string,
): string => {
  switch (severity) {
    case 'TRIP_ANNOTATION_SEVERITY_LOW':
      return t('mapAnnotations_severity_low')
    case 'TRIP_ANNOTATION_SEVERITY_MEDIUM':
      return t('mapAnnotations_severity_medium')
    case 'TRIP_ANNOTATION_SEVERITY_HIGH':
      return t('mapAnnotations_severity_high')
    case 'TRIP_ANNOTATION_SEVERITY_NON_PERFORMANT':
      return t('mapAnnotations_severity_nonPerformant')
    default:
      // A severity the proto gains after this build ships. Naming it "unknown" beats a
      // blank heading, and beats echoing the raw enum name at an operator.
      return t('mapAnnotations_severity_unknown')
  }
}

// A null transcript means four different things. Saying which one keeps a reviewer
// from reading "transcription failed" as "the driver said nothing". Exported so
// AnnotationPopupContent and trip history's Annotations tab can show the same
// placeholder instead of duplicating the switch.
export const annotationTranscriptPlaceholder = (
  t: TFunction,
  transcriptionStatus: string,
): string => {
  switch (transcriptionStatus) {
    case 'TRANSCRIPTION_STATUS_PENDING':
      return t('mapAnnotations_transcript_pending')
    case 'TRANSCRIPTION_STATUS_IN_PROGRESS':
      return t('mapAnnotations_transcript_inProgress')
    case 'TRANSCRIPTION_STATUS_FAILED':
      return t('mapAnnotations_transcript_failed')
    case 'TRANSCRIPTION_STATUS_SKIPPED':
      return t('mapAnnotations_transcript_skipped')
    default:
      return t('mapAnnotations_transcript_none')
  }
}

// What kind of evidence backs a resolution. trip-manager stores this as free
// text, so a type it adds later returns null and the caller shows the bare
// reference rather than an untranslated key. A switch for the scanner's sake,
// like the labels above.
export const annotationEvidenceTypeLabel = (
  t: TFunction,
  evidenceType: string,
): string | null => {
  switch (evidenceType) {
    case 'jira_issue':
      return t('mapAnnotations_resolution_evidence_jiraIssue')
    case 'sw_release':
      return t('mapAnnotations_resolution_evidence_swRelease')
    case 'map_version':
      return t('mapAnnotations_resolution_evidence_mapVersion')
    default:
      return null
  }
}
